
import './utils/browserPolyfills'; // Must be first import
import React, { Suspense, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './index.css';
import { addResourceHints, addInlineCriticalCSS, criticalCSS } from './utils/resourcePriority';
import { initNetworkMonitoring, getNetworkStatus, isLowDataMode } from './utils/networkMonitor';
import { preloadCriticalImages } from './utils/imageOptimizer';
import { AuthProvider } from './contexts/AuthContext';
import { LoadingStrategyProvider } from './components/LoadingStrategyProvider';
import { FontLoadingMonitor } from './components/FontLoadingMonitor';
import { initResourceHints } from './utils/resourceHintsHelper';
import { 
  isLovableEditor, 
  isRecoveryMode, 
  refreshEnvironmentDetection,
  initializeEnvironmentDetection 
} from './utils/environment';
import { LovableEditorDebug } from './components/diagnostics/LovableEditorDebug';
import { detectConnectionSpeed } from './utils/networkSpeedDetector';
import { QUERY_VERSION } from '@/constants/serviceQueryConstants';

// Record timing for performance monitoring
const APP_START_TIME = performance.now();

// CRITICAL: Initialize environment detection as early as possible
// This sets up global state that will persist for the entire app session
console.log('[Main] Initializing environment detection...');
const IS_EDITOR = initializeEnvironmentDetection();
console.log(`[Main] Initial environment detection: ${IS_EDITOR ? 'Lovable Editor' : 'Standard'}`);

// Store detection method for debugging
console.log(`[Main] Detection result: ${IS_EDITOR}`);

// Initialize resource hints as early as possible
initResourceHints();

// Add inline critical CSS for faster painting
addInlineCriticalCSS(criticalCSS);

// Initialize network monitoring ONCE with minimal overhead
// Do this early but with lower priority than critical rendering
const networkMonitorInitialized = initNetworkMonitoring(false);

// Early connection quality check to adapt loading strategy
const checkConnectionQuality = () => {
  const speed = detectConnectionSpeed();
  const isSlow = speed === 'slow' || speed === 'very-slow';
  
  if (isSlow) {
    document.documentElement.classList.add('slow-connection');
    
    // On very slow connections, we'll avoid a lot of background activities
    if (speed === 'very-slow') {
      document.documentElement.classList.add('minimal-mode');
    }
  }
  
  return isSlow;
};

// Skip expensive operations in Lovable editor
const isSlow = IS_EDITOR ? false : checkConnectionQuality();
const isLowData = IS_EDITOR ? false : isLowDataMode();

// Global recovery handler for HMR issues
if (import.meta.hot) {
  import.meta.hot.on('vite:beforeUpdate', () => {
    console.log('[HMR] Update detected, preparing for update...');
    
    // Mark that we're in an HMR update to help with recovery
    try {
      sessionStorage.setItem('lamulamu_hmr_updating', 'true');
      
      // Preserve editor detection state
      const editorState = localStorage.getItem('lamulamu_is_lovable_editor');
      if (editorState) {
        sessionStorage.setItem('lamulamu_editor_before_hmr', editorState);
        console.log('[HMR] Preserved editor state:', editorState);
      }
    } catch (e) {
      // Ignore storage errors
    }
  });
  
  import.meta.hot.on('vite:afterUpdate', () => {
    console.log('[HMR] Update completed');
    
    // Reset the HMR updating flag
    try {
      sessionStorage.removeItem('lamulamu_hmr_updating');
      
      // Check if environment detection changed during HMR
      const previousState = sessionStorage.getItem('lamulamu_editor_before_hmr');
      const currentState = localStorage.getItem('lamulamu_is_lovable_editor');
      
      if (previousState && previousState !== currentState) {
        console.warn('[HMR] Editor detection changed during HMR!', {
          before: previousState,
          after: currentState
        });
        
        // Restore previous state to localStorage
        localStorage.setItem('lamulamu_is_lovable_editor', previousState);
        
        console.log('[HMR] Restored previous editor state');
      }
      
      // Clean up temporary storage
      sessionStorage.removeItem('lamulamu_editor_before_hmr');
    } catch (e) {
      // Ignore storage errors
    }
  });
  
  import.meta.hot.on('vite:error', (error) => {
    console.error('[HMR] Update error:', error);
    
    // If in Lovable editor, set recovery mode
    if (IS_EDITOR) {
      try {
        sessionStorage.setItem('lamulamu_recovery_mode', 'true');
        sessionStorage.setItem('lamulamu_hmr_error', JSON.stringify(error));
      } catch (e) {
        // Ignore storage errors
      }
    }
  });
}

// Create a single shared query client instance with connection-aware settings
// Enhanced with better timeout handling and Lovable editor detection
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: isSlow ? 1 : IS_EDITOR ? 0 : 2,
      retryDelay: attempt => Math.min(1000 * 2 ** attempt, 30000),
      refetchOnWindowFocus: IS_EDITOR ? false : !isSlow,
      refetchOnMount: IS_EDITOR ? false : !isSlow,
      staleTime: IS_EDITOR ? Infinity : (isSlow ? 15 * 60 * 1000 : 5 * 60 * 1000), // In editor, keep forever
      gcTime: IS_EDITOR ? Infinity : (isSlow ? 30 * 60 * 1000 : 15 * 60 * 1000),
      networkMode: IS_EDITOR ? 'always' : 'online',
    },
  },
});

// Reset the React Query cache when in recovery mode OR on version mismatch
// This ensures stale queries related to outdated schema don't cause issues
const shouldResetCache = () => {
  try {
    // Check for recovery mode
    if (isRecoveryMode()) {
      console.log('[Recovery] Detected recovery mode - will reset cache');
      return true;
    }
    
    // Check for version mismatch
    const cachedVersion = localStorage.getItem('lamulamu_query_version');
    const currentVersion = QUERY_VERSION; // Use the constant instead of hardcoding
    
    if (cachedVersion && cachedVersion !== currentVersion) {
      console.log(`[Cache] Version mismatch - stored: ${cachedVersion}, current: ${currentVersion}`);
      localStorage.setItem('lamulamu_query_version', currentVersion);
      return true;
    } else if (!cachedVersion) {
      // First time initialization
      localStorage.setItem('lamulamu_query_version', currentVersion);
    }
    
    return false;
  } catch (e) {
    // If any error occurs, reset cache to be safe
    console.warn('[Cache] Error checking version, resetting cache:', e);
    return true;
  }
};

// Reset cache if needed
if (shouldResetCache()) {
  console.log('[Cache] Resetting React Query cache');
  queryClient.clear();
  
  // Also clear localStorage cache keys that might contain outdated data
  try {
    // Clear only our app-specific keys
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('lamulamu_') && key !== 'lamulamu_query_version') {
        localStorage.removeItem(key);
      }
    });
    console.log('[Cache] Cleared localStorage cache');
  } catch (e) {
    console.warn('[Cache] Error clearing localStorage:', e);
  }
}

// Add a schema version checker component to force refresh when schema changes
const SchemaVersionChecker = () => {
  useEffect(() => {
    // This runs once on component mount to check schema version
    const checkSchemaVersion = async () => {
      try {
        const cachedSchemaVersion = localStorage.getItem('lamulamu_schema_version');
        // If no cached version or it doesn't match current version, clean up caches
        if (!cachedSchemaVersion || cachedSchemaVersion !== QUERY_VERSION) {
          console.log(`[Schema] Version mismatch - stored: ${cachedSchemaVersion}, current: ${QUERY_VERSION}`);
          
          // Remove any service-related queries
          queryClient.removeQueries({ queryKey: ['services'] });
          queryClient.removeQueries({ queryKey: ['service'] });
          
          // Update the stored version
          localStorage.setItem('lamulamu_schema_version', QUERY_VERSION);
          
          // Clear browser cache for key API endpoints
          if ('caches' in window) {
            try {
              // Try to clear API cache entries
              const cache = await caches.open('api-cache');
              const keys = await cache.keys();
              const serviceRequests = keys.filter(key => 
                key.url.includes('/services') || 
                key.url.includes('/service_images')
              );
              
              for (const request of serviceRequests) {
                await cache.delete(request);
              }
              
              console.log(`[Cache] Cleared ${serviceRequests.length} API cache entries`);
            } catch (err) {
              console.warn('[Cache] Error clearing Cache API:', err);
            }
          }
        }
      } catch (e) {
        console.warn('[Schema] Error checking schema version:', e);
      }
    };
    
    checkSchemaVersion();
  }, []);
  
  return null;
};

// Basic fallback component for slow connections
const FallbackLoadingComponent = () => {
  const speed = detectConnectionSpeed();
  const isVerySlow = speed === 'very-slow';
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
      <div className="w-20 h-20 mb-4 relative">
        <img 
          src="/lovable-uploads/98a9fcc4-c199-4be7-8bad-6756a3aa8870.png"
          alt="Loading..."
          className="w-full h-full object-contain"
          width="80"
          height="80"
        />
      </div>
      <h2 className="text-xl font-medium mb-2 text-center">LamuLamu</h2>
      <p className="text-gray-500 text-center">
        {isVerySlow 
          ? "Loading on slow connection, please be patient..." 
          : "Loading, please wait..."}
      </p>
      <div className="mt-4 w-full max-w-xs bg-gray-200 h-1.5 rounded-full overflow-hidden">
        <div className="h-full bg-primary animate-pulse rounded-full" style={{width: '60%'}}></div>
      </div>
    </div>
  );
};

// Lovable editor specific loading component
const LovableEditorLoadingComponent = () => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4">
    <div className="w-20 h-20 mb-4">
      <img 
        src="/lovable-uploads/98a9fcc4-c199-4be7-8bad-6756a3aa8870.png"
        alt="Loading in Lovable Editor"
        className="w-full h-full object-contain"
      />
    </div>
    <h2 className="text-xl font-medium text-center mb-1">LamuLamu</h2>
    <p className="text-sm text-blue-600 font-medium">Loading in Lovable Editor</p>
    <div className="mt-2 text-xs text-gray-400">Using optimized editor mode</div>
  </div>
);

// Choose appropriate fallback based on environment
const LoadingComponent = IS_EDITOR ? LovableEditorLoadingComponent : FallbackLoadingComponent;

// Safer initialization process
function SafeApp() {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('Global error caught:', event.error);
      setHasError(true);
      setErrorMessage(event.message || 'Unknown error occurred');
    };
    
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);
  
  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
          <h2 className="text-xl font-bold text-red-600 mb-4">Something went wrong</h2>
          <p className="text-gray-700 mb-4">
            The application encountered an error. Please try refreshing the page.
          </p>
          {errorMessage && (
            <pre className="bg-gray-100 p-3 rounded text-sm overflow-auto max-h-40 mb-4">
              {errorMessage}
            </pre>
          )}
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <LoadingStrategyProvider>
            <Suspense fallback={<LoadingComponent />}>
              <SchemaVersionChecker />
              <FontLoadingMonitor />
              <AuthProvider>
                <App />
              </AuthProvider>
            </Suspense>
          </LoadingStrategyProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>
  );
}

// Improved React root creation with better error handling
ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <SafeApp />
);

// Register service worker for PWA features
// Enable in preview/editor for PWA testing
console.log('Attempting to register service worker...');
setTimeout(() => {
  import('./utils/serviceWorkerRegistration').then(({ registerServiceWorker }) => {
    console.log('Service worker registration module loaded');
    // Register service worker in both development and production for PWA testing
    // Force registration even in editor for PWA preview functionality
    registerServiceWorker(true);
  }).catch(err => {
    console.error('Failed to load service worker registration:', err);
  });
}, 2000);

// Declare requestIdleCallback for TypeScript
declare global {
  interface Window {
    requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
    __moduleErrorListenerAdded?: boolean;
  }
}
